// framework/client/components/Signup.js
import { ComponentBase } from "../core/ComponentBase.js";
import { z } from "zod";

export class Signup extends ComponentBase {

  constructor(config) {
    super(config);
    this.container = document.createElement("div");
    this.container.className = "signup-container";

    this.wizardState = {
      currentStep: 'details', // 'details', 'verify-email', 'verify-phone'
      formData: {},
      otpRequestId: null,
      verificationIdentifier: null,
      verificationChannel: null,
    };

    this.formRenderer = null;
    this._boundHandleSubmit = this.handleFormSubmit.bind(this);
    this.emailSignupVisible = true; // State to control form visibility
  }

  async initialize() {
    this.wizardState = {
      currentStep: 'details',
      formData: {},
      verifications: {
        email: { otpRequestId: null, code: null, verified: false },
        phone: { otpRequestId: null, code: null, verified: false }
      },
    };

    this.sessionTokenHandler = this.appClient.sessionTokenHandler;
    const clientConfig = await this.sessionTokenHandler.getClientConfig();
    this.signupConfig = clientConfig.auth?.signup || {};
    this.verificationConfig = this.signupConfig.verification || { mode: 'disabled' };

    if (this.signupConfig.inviteCode?.enabled) {
      this.signupConfig.fields = this.signupConfig.fields || [];
      this.signupConfig.fields.push({
        name: "inviteCode", type: "text", label: "Invite Code",
        required: this.signupConfig.inviteCode.required, autocomplete: "off"
      });
    }

    await super.initialize();
    this._handleUrlParams();
  }

  _handleUrlParams() {
    const queryParams = this.appClient.layoutEngine.getQueryParams();
    const inviteCodeFromUrl = queryParams.invite;
    if (inviteCodeFromUrl) {
      this.wizardState.formData.inviteCode = inviteCodeFromUrl;
    }
  }

  generateSchemaForStep(step) {
    const schemaDefinition = {};
    if (step === 'details') {
        const fieldConfigs = this.signupConfig.fields || [];
        fieldConfigs.forEach(field => {
            let fieldSchema = z.string();
            if (field.required) fieldSchema = fieldSchema.min(1, { message: `${field.label} is required.` });
            else fieldSchema = fieldSchema.optional().nullable();

            if (field.type === 'email') fieldSchema = fieldSchema.email({ message: "Please enter a valid email address." });
            if (field.name === 'password') fieldSchema = fieldSchema.min(8, { message: "Password must be at least 8 characters." });
            if (field.name === 'phoneNumber') fieldSchema = fieldSchema.regex(/^\+?[1-9]\d{1,14}$/, { message: "Please enter a valid phone number with country code." });

            schemaDefinition[field.name] = fieldSchema;
        });

    } else if (step.startsWith('verify')) {
        schemaDefinition.otpCode = z.string().length(6, { message: "Code must be 6 digits." });
    }
    return z.object(schemaDefinition);
}


  async render() {
    this.container.innerHTML = '';
    if (this.wizardState.currentStep === 'details') {
      await this.renderDetailsStep();
    } else if (this.wizardState.currentStep === 'verify-email') {
      await this.renderVerifyStep('email');
    } else if (this.wizardState.currentStep === 'verify-phone') {
      await this.renderVerifyStep('phone');
    }
    return this.container;
  }

  _createOAuthProviderButtons() {
    const oauthConfig = this.sessionTokenHandler.clientConfig?.auth?.oauth || {};
    const enabledProviders = Object.entries(oauthConfig).filter(([name, config]) => config.enabled && name.indexOf('channel') < 0);

    if (enabledProviders.length === 0) return null;

    const container = document.createElement('div');
    container.className = 'd-grid gap-2 mb-1';

    enabledProviders.forEach(([providerName, config]) => {
        const providerTitle = providerName.charAt(0).toUpperCase() + providerName.slice(1);
        const textContent = `Continue with ${providerTitle}`;

        // Create a wrapper for each button+logo combo
        const wrapper = document.createElement('div');
        wrapper.style.position = 'relative';

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'btn btn-outline-secondary w-100';
        button.textContent = textContent;
        button.title = `Sign up with ${providerTitle}`;
        button.setAttribute('data-i18n', textContent);

        let logoUrl = '';
        if (providerName === 'google') {
            logoUrl = '/assets/icons/google-logo.svg';
        } else if (providerName === 'microsoft') {
            logoUrl = '/assets/icons/microsoft-logo.svg';
        }

        if (logoUrl) {
            const img = document.createElement('img');
            img.src = logoUrl;
            img.style.position = 'absolute';
            img.style.left = '1rem';
            img.style.top = '50%';
            img.style.transform = 'translateY(-50%)';
            img.style.height = '1.25em';
            img.style.width = '1.25em';
            wrapper.appendChild(img);
        } else {
            // Fallback for providers without a special logo
            button.classList.add('d-flex', 'align-items-center', 'justify-content-center');
            button.innerHTML = `<i class="bi bi-${providerName} me-2"></i> ${textContent}`;
        }

        button.addEventListener('click', async () => {
            try {
                await this.startOAuthLogin(providerName);
            } catch (error) {
                this.log.error(`OAuth login failed for ${providerName}:`, error);
                this.appClient.notifier.error(`Could not complete ${providerName} login.`);
                await this.render();
            }
        });

        wrapper.appendChild(button);
        container.appendChild(wrapper);
    });

    return container;
  }

  isNativeExternalOAuthAvailable() {
    if (typeof window === 'undefined') return false;
    if (window.electronAPI && typeof window.electronAPI.openExternalUrl === 'function') return true;
    if (window.Capacitor?.isNativePlatform?.()) return true;
    return window.parent && window.parent !== window;
  }

  async openExternalOAuthUrl(url) {
    if (window.electronAPI && typeof window.electronAPI.openExternalUrl === 'function') {
      return window.electronAPI.openExternalUrl(url);
    }

    const capacitorBrowser = window.Capacitor?.Plugins?.Browser || window.CapacitorBrowser;
    if (window.Capacitor?.isNativePlatform?.() && capacitorBrowser?.open) {
      await capacitorBrowser.open({ url });
      return { success: true };
    }

    if (window.parent && window.parent !== window) {
      return this.openExternalOAuthUrlViaParent(url);
    }

    return { success: false, error: 'No native external browser bridge is available.' };
  }

  openExternalOAuthUrlViaParent(url) {
    return new Promise((resolve) => {
      const requestId = `oauth-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const timeout = setTimeout(() => {
        window.removeEventListener('message', onMessage);
        resolve({ success: false, error: 'Timed out waiting for native browser bridge.' });
      }, 10000);

      const onMessage = (event) => {
        if (event.source !== window.parent || event.data?.type !== 'appbuilder:openExternalUrlResult' || event.data?.requestId !== requestId) {
          return;
        }

        clearTimeout(timeout);
        window.removeEventListener('message', onMessage);
        resolve(event.data.result || { success: true });
      };

      window.addEventListener('message', onMessage);
      window.parent.postMessage({ type: 'appbuilder:openExternalUrl', requestId, url }, '*');
    });
  }

  async startOAuthLogin(providerName) {
    if (!this.isNativeExternalOAuthAvailable()) {
      const result = await this.appClient.rpcClient.call('AuthRpc.getOAuthLoginUrl', { provider: providerName });
      if (result.url) {
        // Web and Capacitor keep the normal redirect-based OAuth behavior.
        window.location.href = result.url;
      }
      return;
    }

    const externalSession = await this.appClient.rpcClient.call('AuthRpc.createExternalOAuthLoginSession', { provider: providerName });
    if (!externalSession?.authUrl || !externalSession?.pollToken) {
      throw new Error('External OAuth session could not be created.');
    }

    const openResult = await this.openExternalOAuthUrl(externalSession.authUrl);
    if (openResult && openResult.success === false) {
      throw new Error(openResult.error || 'Could not open the system browser.');
    }

    this.renderLoadingState("Complete sign-in in your browser...");
    const sessionPayload = await this.pollExternalOAuthLogin(externalSession.pollToken, externalSession.expiresInMs);
    await this.appClient.sessionTokenHandler.processLoginSuccess(sessionPayload);
  }

  async pollExternalOAuthLogin(pollToken, expiresInMs = 10 * 60 * 1000) {
    const deadline = Date.now() + expiresInMs;

    while (Date.now() < deadline) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      const result = await this.appClient.rpcClient.call('AuthRpc.pollExternalOAuthLoginSession', { pollToken });

      if (result.status === 'pending') {
        continue;
      }

      if (result.status === 'complete' && result.sessionPayload) {
        return result.sessionPayload;
      }

      throw new Error(result.error || `OAuth login ${result.status || 'failed'}.`);
    }

    throw new Error('OAuth login timed out.');
  }

  renderLoadingState(message = "Finalizing login...") {
    if (!this.container) return;
    this.container.innerHTML = `
      <div class="d-flex justify-content-center align-items-center" style="min-height: 300px;">
        <div class="text-center">
          <div class="spinner-border text-primary" role="status"></div>
          <p class="mt-3 text-muted">${this.t(message)}</p>
        </div>
      </div>
    `;
  }

  _createSeparator() {
      const separator = document.createElement('div');
      separator.className = 'd-flex align-items-center my-1';
      separator.innerHTML = '<hr class="flex-grow-1"><span class="mx-2 text-muted small">OR</span><hr class="flex-grow-1">';
      return separator;
  }

  async renderDetailsStep() {
    this.container.innerHTML = ''; // Clear previous content

    const titleElement = document.createElement('h2');
    titleElement.className = 'card-title text-center fs-4 fw-bold mb-2';
    titleElement.textContent = this.t('Create an Account');
    this.container.appendChild(titleElement);

    // OAuth Buttons
    const oauthButtons = this._createOAuthProviderButtons();
    if (oauthButtons) {
        this.container.appendChild(oauthButtons);
    }

    // Email Form Section
    if (this.emailSignupVisible) {
        if (oauthButtons) {
            this.container.appendChild(this._createSeparator());
        }
        if (this.formRenderer) this.formRenderer.destroy();

        const formConfig = {
            labelMode: 'floating',
            gapClass: 'mb-2',
            ...this.signupConfig,
            fields: [...(this.signupConfig.fields || [])],
            submitButtonLabel: "Continue",
            title: '', // Title is already rendered outside the form
        };

        this.formRenderer = await this.createManagedComponent('GenericFormRenderer', null, {
            instanceId: `${this.config.instanceId}-details-form`,
            formId: 'signupDetailsForm',
            formConfig: formConfig,
            schema: this.generateSchemaForStep('details'),
            compact: true
        });

        const formElement = await this.formRenderer.render();
        this.formRenderer.on('formSubmit', this._boundHandleSubmit);
        this.formRenderer.setData(this.wizardState.formData);
        this.container.appendChild(formElement);

        await this._ensureTurnstile(this.container);

    } else {
        if (oauthButtons) {
            this.container.appendChild(this._createSeparator());
        }
        const emailButton = document.createElement('button');
        emailButton.textContent = 'Continue with Email';
        emailButton.className = 'btn btn-secondary w-100';
        emailButton.onclick = () => {
            this.emailSignupVisible = true;
            this.render();
        };
        this.container.appendChild(emailButton);
    }
}

  async _ensureTurnstile(parentContainer) {
    try {
      const clientConfig = await this.sessionTokenHandler.getClientConfig();
      const siteKey = clientConfig.turnstile?.siteKey;
      if (!siteKey) return;

      if (!window.turnstile) {
        if (!document.getElementById('cf-turnstile-script')) {
          const script = document.createElement('script');
          script.id = 'cf-turnstile-script';
          script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
          script.async = true;
          document.head.appendChild(script);
        }

        let attempts = 0;
        while (!window.turnstile && attempts < 30) {
          await new Promise(r => setTimeout(r, 100));
          attempts++;
        }
      }

      if (!window.turnstile) return;

      let widgetContainer = parentContainer.querySelector('.cf-turnstile-slot');
      if (!widgetContainer) {
        widgetContainer = document.createElement('div');
        widgetContainer.className = 'cf-turnstile-slot d-flex justify-content-center my-1';
        // Insert right before the submit button if formRenderer container exists
        if (this.formRenderer?.formElement) {
          const submitBtn = this.formRenderer.formElement.querySelector('button[type="submit"]');
          if (submitBtn?.parentElement) {
            submitBtn.parentElement.before(widgetContainer);
          } else {
            this.formRenderer.formElement.appendChild(widgetContainer);
          }
        } else {
          parentContainer.appendChild(widgetContainer);
        }
      }

      this.turnstileWidgetId = window.turnstile.render(widgetContainer, {
        sitekey: siteKey,
        theme: 'auto',
        callback: (token) => {
          this.turnstileToken = token;
        }
      });
    } catch (err) {
      this.log.debug(`[Signup] Turnstile mount note: ${err.message}`);
    }
  }


  async renderVerifyStep(channel) {
    if (this.formRenderer) this.formRenderer.destroy();
    const identifier = channel === 'email' ? this.wizardState.formData.email : this.wizardState.formData.phoneNumber;
    this.wizardState.verificationIdentifier = identifier;
    this.wizardState.verificationChannel = channel;

    const channelName = channel === 'phone' ? 'Phone Number' : 'Email Address';
    const headerContainer = document.createElement('div');
    headerContainer.className = 'text-center mb-3';
    headerContainer.innerHTML = `
        <h3 class="fw-bold mb-1">${this.t(`Verify Your ${channelName}`)}</h3>
        <p class="text-muted small mb-0">${this.t("A 6-digit verification code has been sent to")} <strong class="text-body">${identifier}</strong>.</p>
    `;
    this.container.appendChild(headerContainer);

    const submitButtonLabel = (this.verificationConfig.phone?.enabled && channel === 'email')
      ? "Continue to Phone Verification"
      : "Verify & Create Account";

    const formConfig = {
      labelMode: 'floating',
      gapClass: 'mb-1',
      submitButtonLabel: this.t(submitButtonLabel),
      fields: [{ name: "otpCode", type: "otp", label: "6-Digit Code", required: true, autocomplete: "one-time-code" }],
      validation: { validateOnChange: true }
    };
    this.formRenderer = await this.createManagedComponent('GenericFormRenderer', null, {
      instanceId: `${this.config.instanceId}-verify-${channel}-form`,
      formId: `signupVerify${channel}Form`,
      formConfig: formConfig,
      schema: this.generateSchemaForStep('verify'),
      compact: true
    });
    const formElement = await this.formRenderer.render();
    this.formRenderer.on('formSubmit', this._boundHandleSubmit);

    const resendContainer = document.createElement('div');
    resendContainer.className = 'text-center mt-3';
    const resendButton = document.createElement('button');
    resendButton.type = 'button';
    resendButton.className = 'btn btn-link';
    resendButton.textContent = this.t('Did not receive a code? Resend');

    let countdownInterval = null;
    const cooldownSeconds = 60;

    const startCooldown = () => {
      let secondsRemaining = cooldownSeconds;
      resendButton.disabled = true;
      if (countdownInterval) clearInterval(countdownInterval);
      resendButton.textContent = this.t(`Resend in {{seconds}}s`, { seconds: secondsRemaining });
      countdownInterval = setInterval(() => {
        secondsRemaining--;
        if (secondsRemaining > 0) {
          resendButton.textContent = this.t(`Resend in {{seconds}}s`, { seconds: secondsRemaining });
        } else {
          clearInterval(countdownInterval);
          resendButton.disabled = false;
          resendButton.textContent = this.t('Did not receive a code? Resend');
        }
      }, 1000);
    };

    resendButton.addEventListener('click', async () => {
      startCooldown();
      try {
        const result = await this.appClient.rpcClient.call('AuthRpc.requestVerificationCode', {
          channelType: channel === 'phone' ? (this.verificationConfig.phone.channels[0] || 'sms') : 'email',
          identifier: this.wizardState.verificationIdentifier,
          contactInfo: {
            firstName: this.wizardState.formData.firstName,
            lastName: this.wizardState.formData.lastName
          }
        });
        if (result.success) {
          // --- START OF FIX: Update the correct otpRequestId in the wizard state ---
          this.wizardState.verifications[channel].otpRequestId = result.otpRequestId;
          // --- END OF FIX ---
          this.appClient.notifier.success("A new verification code has been sent.");
        } else {
          clearInterval(countdownInterval);
          resendButton.disabled = false;
          resendButton.textContent = this.t('Did not receive a code? Resend');
          this.appClient.notifier.error("Failed to send a new code. Please try again.");
        }
      } catch (error) {
        clearInterval(countdownInterval);
        resendButton.disabled = false;
        resendButton.textContent = this.t('Did not receive a code? Resend');
        this.appClient.notifier.error(error.message);
      }
    });

    resendContainer.appendChild(resendButton);
    formElement.appendChild(resendContainer);
    this.container.appendChild(formElement);
  }


  async handleFormSubmit() {
    if (await this.formRenderer.isFormValid()) {
      if (this.wizardState.currentStep === 'details') {
        this.wizardState.formData = this.formRenderer.getFormData();
        await this.processDetailsStep();
      } else {
        const { otpCode } = this.formRenderer.getFormData();
        await this.processVerifyStep(otpCode);
      }
    } else {
      this.log.warn("Submit blocked due to form validation errors.");
    }
  }

      async processDetailsStep() {
        try {
            this.formRenderer.setSubmitting(true);
            await this.appClient.rpcClient.call('AuthRpc.preSignupValidate', this.wizardState.formData);
            
            this.log.info("Pre-signup validation successful.");
            if (this.verificationConfig.mode !== 'pre-signup') {
                await this.performSignup(this.wizardState.formData);
                return;
            }
            if (this.verificationConfig.email?.enabled) {
                await this.requestAndTransitionToVerify('email');
            } else if (this.verificationConfig.phone?.enabled) {
                await this.requestAndTransitionToVerify('phone');
            } else {
                await this.performSignup(this.wizardState.formData);
            }

        } catch (error) {
            if (error && error.type === 'validation' && error.fields) {
                this.formRenderer.setFieldErrors(error.fields);
                this.appClient.notifier.error(error.message || "Please correct the errors below.", { title: "Validation Failed" });
            } else {
                const errorMessage = error?.message || "An unknown error occurred. Please try again.";
                this.appClient.notifier.error(errorMessage, { title: "Error" });
            }
            this.formRenderer.setSubmitting(false);
        }
    }



  async requestAndTransitionToVerify(channel) {
    const identifier = channel === 'email' ? this.wizardState.formData.email : this.wizardState.formData.phoneNumber;
    this.wizardState.verificationChannel = channel;
    try {
      this.formRenderer.setSubmitting(true);
      const result = await this.appClient.rpcClient.call('AuthRpc.requestVerificationCode', {
        channelType: channel === 'phone' ? (this.verificationConfig.phone.channels[0] || 'sms') : 'email',
        identifier,
        turnstileToken: this.turnstileToken || null,
        contactInfo: { firstName: this.wizardState.formData.firstName, lastName: this.wizardState.formData.lastName }
      });

      if (result.success) {
        // --- THIS IS THE CRITICAL FIX ---
        // Store the received otpRequestId in the correct nested object for the current channel.
        this.wizardState.verifications[channel].otpRequestId = result.otpRequestId;
        // --- END OF CRITICAL FIX ---

        this.wizardState.currentStep = `verify-${channel}`;
        this.render(); // Re-render to show the verification step UI
      } else {
        // Handle cases where Omnix might return success: false
        throw new Error(result.error || "Failed to send verification code.");
      }
    } catch (error) {
      this.appClient.notifier.error(`Could not send verification code: ${error.message}`);
      this.formRenderer.setSubmitting(false);
    }
  }

  async processVerifyStep(otpCode) {
    const channel = this.wizardState.verificationChannel; // 'email' or 'phone'
    const otpRequestId = this.wizardState.verifications[channel].otpRequestId;

    try {
      this.formRenderer.setSubmitting(true);

      const result = await this.appClient.rpcClient.call('AuthRpc.verifyCode', {
        otpRequestId: otpRequestId,
        code: otpCode,
        finalize: false // Just check if the code is valid, don't consume it yet.
      });

      if (!result.verified) {
        throw new Error("The verification code is incorrect or has expired.");
      }

      this.wizardState.verifications[channel].verified = true;
      this.wizardState.verifications[channel].code = otpCode;

      if (channel === 'email' && this.verificationConfig.phone?.enabled) {
        // If email was just verified and phone is next, transition to phone verification
        await this.requestAndTransitionToVerify('phone');
      } else {
        // This was the final verification step, proceed to create the account
        await this.performSignup(this.wizardState.formData);
      }

    } catch (error) {
      this.appClient.notifier.error(`Verification failed: ${error.message}`);
      this.formRenderer.setSubmitting(false);
    }
  }



  async performSignup(signupData) {
    const finalSignupData = { ...signupData };
    if (this.turnstileToken) {
      finalSignupData.turnstileToken = this.turnstileToken;
    }

    if (this.verificationConfig.mode === 'pre-signup') {
      finalSignupData.verifications = {};
      if (this.verificationConfig.email?.enabled) {
        if (!this.wizardState.verifications.email.verified) {
          this.appClient.notifier.error("Email has not been verified.");
          this.formRenderer.setSubmitting(false);
          return;
        }
        finalSignupData.verifications.email = {
          otpRequestId: this.wizardState.verifications.email.otpRequestId,
          code: this.wizardState.verifications.email.code
        };
      }
      if (this.verificationConfig.phone?.enabled) {
        if (!this.wizardState.verifications.phone.verified) {
          this.appClient.notifier.error("Phone has not been verified.");
          this.formRenderer.setSubmitting(false);
          return;
        }
        finalSignupData.verifications.phone = {
          otpRequestId: this.wizardState.verifications.phone.otpRequestId,
          code: this.wizardState.verifications.phone.code
        };
      }
    }

    try {
      this.formRenderer.setSubmitting(true);
      // Step 1: Sign up the user
      await this.sessionTokenHandler.signup(finalSignupData);

      // Step 2: Log the new user in
      const loginIdentifier = finalSignupData.email || finalSignupData.username;

      await this.sessionTokenHandler.login(loginIdentifier, finalSignupData.password);

    } catch (apiError) {
      this.formRenderer.setSubmitting(false);
      this.appClient.notifier.error(apiError.message, { title: "Signup Error" });
      this.wizardState.currentStep = 'details';
      this.render();
    }
  }

  destroy() {
    if (this.formRenderer) {
      this.formRenderer.off('formSubmit', this._boundHandleSubmit);
      this.formRenderer.destroy();
    }
    super.destroy();
  }
}
